'use client';

import {
  createContext,
  Dispatch,
  ReactNode,
  useContext,
  useReducer,
} from 'react';
import type { Wx } from './weather/types';

export type FlowPhase = 'intro' | 'name' | 'sky' | 'insights';

export type FlowState = {
  phase: FlowPhase;
  userName: string;
  wx: Wx | null;
  /** performance.now() at the moment weather resolved (or skip) — anchors the deck's clock. */
  wxAt: number;
  /** True the instant the hold-to-charge burst fires (sky fades in, orb detaches). */
  revealed: boolean;
  /** True ~350ms after the burst, once the orb has translated to its perched position. */
  perched: boolean;
};

export type FlowAction =
  | { type: 'REVEAL' }
  | { type: 'PERCH' }
  | { type: 'BURST' }
  | { type: 'SUBMIT_NAME'; name: string }
  | { type: 'SKIP_NAME' }
  | { type: 'ENTER_INSIGHTS'; wx: Wx | null };

const initialState: FlowState = {
  phase: 'intro',
  userName: '',
  wx: null,
  wxAt: 0,
  revealed: false,
  perched: false,
};

function flowReducer(state: FlowState, action: FlowAction): FlowState {
  switch (action.type) {
    case 'REVEAL':
      return state.revealed ? state : { ...state, revealed: true };
    case 'PERCH':
      return state.perched ? state : { ...state, perched: true };
    case 'BURST':
      return state.phase === 'intro' ? { ...state, phase: 'name' } : state;
    case 'SUBMIT_NAME':
      return state.phase === 'name'
        ? {
            ...state,
            phase: 'sky',
            userName: action.name.trim().replace(/\s+/g, ' ').slice(0, 20),
          }
        : state;
    case 'SKIP_NAME':
      return state.phase === 'name'
        ? { ...state, phase: 'sky', userName: '' }
        : state;
    case 'ENTER_INSIGHTS':
      return state.phase === 'sky'
        ? {
            ...state,
            phase: 'insights',
            wx: action.wx,
            wxAt: performance.now(),
          }
        : state;
    default:
      return state;
  }
}

const FlowStateContext = createContext<FlowState | null>(null);
const FlowDispatchContext = createContext<Dispatch<FlowAction> | null>(null);

export const FlowProvider = ({ children }: { children: ReactNode }) => {
  const [state, dispatch] = useReducer(flowReducer, initialState);

  return (
    <FlowStateContext.Provider value={state}>
      <FlowDispatchContext.Provider value={dispatch}>
        {children}
      </FlowDispatchContext.Provider>
    </FlowStateContext.Provider>
  );
};

export function useFlowState(): FlowState {
  const ctx = useContext(FlowStateContext);
  if (!ctx) throw new Error('useFlowState must be used within FlowProvider');
  return ctx;
}

export function useFlowDispatch(): Dispatch<FlowAction> {
  const ctx = useContext(FlowDispatchContext);
  if (!ctx) {
    throw new Error('useFlowDispatch must be used within FlowProvider');
  }
  return ctx;
}
